import { useMemo } from 'react';
import { TrendingUp, Flame } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useWishlist } from '@/data/hooks/useWishlist';
import type { WishlistItem } from '@/types';

interface DemandRow {
  vehicle: string;
  count: number;
  hot: number;
  warm: number;
  cold: number;
}

function aggregate(items: WishlistItem[]): DemandRow[] {
  const map = new Map<string, DemandRow>();
  for (const w of items) {
    if (w.status !== 'active') continue;
    const vehicle = `${w.make}${w.model ? ' ' + w.model : ''}`.trim();
    if (!vehicle) continue;
    const row = map.get(vehicle) ?? { vehicle, count: 0, hot: 0, warm: 0, cold: 0 };
    row.count += 1;
    if (w.urgency === 'hot') row.hot += 1;
    else if (w.urgency === 'cold') row.cold += 1;
    else row.warm += 1;
    map.set(vehicle, row);
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count).slice(0, 10);
}

export function CustomerDemandCard() {
  const { items, loading } = useWishlist();
  const rows = useMemo(() => aggregate(items), [items]);

  return (
    <Card className="border-border/60 bg-card/80">
      <div className="flex items-center justify-between border-b border-border/60 px-5 py-3.5">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold uppercase tracking-wider">Customer Demand</h2>
        </div>
        <span className="text-xs text-muted-foreground">Top requested vehicles</span>
      </div>

      {loading ? (
        <div className="px-5 py-6 text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-muted-foreground">
          No customer requests logged yet. Reps can add them from their dashboard.
        </div>
      ) : (
        <ul className="divide-y divide-border/60">
          {rows.map((row, idx) => (
            <li key={row.vehicle} className="flex items-center gap-3 px-5 py-3">
              <span className="w-6 text-xs font-bold text-muted-foreground tabular-nums">
                {String(idx + 1).padStart(2, '0')}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{row.vehicle}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {row.hot > 0 && (
                    <span className="inline-flex items-center gap-1 mr-2">
                      <Flame className="h-3 w-3 text-primary" />
                      {row.hot} hot
                    </span>
                  )}
                  {row.warm > 0 && <span className="mr-2">{row.warm} warm</span>}
                  {row.cold > 0 && <span>{row.cold} cold</span>}
                </p>
              </div>
              <span className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-md bg-primary/15 text-primary text-sm font-bold tabular-nums">
                {row.count}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}